import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import CategoryBadge from './CategoryBadge';

const HeroContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
    gap: 2.5rem;
  }
  
  @media (min-width: 1440px) {
    gap: 3rem;
  }
`;

// Left Side - Carousel
const CarouselContainer = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  overflow: hidden;
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.soft};
`;

const CarouselWrapper = styled.div`
  position: relative;
  height: 20rem;
  overflow: hidden;
  
  @media (min-width: 640px) {
    height: 24rem;
  }
  
  @media (min-width: 768px) {
    height: 28rem;
  }
  
  @media (min-width: 1024px) {
    height: 32rem;
  }
  
  @media (min-width: 1440px) {
    height: 36rem;
  }
  
  @media (min-width: 1920px) {
    height: 40rem;
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.$currentIndex * 100}%);
  height: 100%;
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  height: 100%;
  position: relative;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CarouselOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
`;

const CarouselContent = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 2rem;
  z-index: 10;
  text-align: left;
  width: 100%;
  max-width: 100%;
  
  @media (min-width: 640px) {
    padding: 2.5rem;
    max-width: 95%;
  }
  
  @media (min-width: 768px) {
    padding: 3rem;
    max-width: 90%;
  }
  
  @media (min-width: 1024px) {
    padding: 3.5rem;
    max-width: 85%;
  }
  
  @media (min-width: 1440px) {
    padding: 4rem;
    max-width: 80%;
  }
  
  @media (min-width: 1920px) {
    max-width: 75%;
  }
`;

const CarouselBadge = styled.div`
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: flex-start;
  
  @media (min-width: 1024px) {
    margin-bottom: 1rem;
  }
`;

const CarouselTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  line-height: 1.3;
  color: #ffffff;
  margin-bottom: 0.75rem;
  white-space: normal;
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  @media (min-width: 640px) {
    font-size: 1.75rem;
  }
  
  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2.25rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 3rem;
  }
`;

const CarouselMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #ffffffcc;
  
  @media (min-width: 768px) {
    font-size: 0.9375rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`;

const CarouselNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background-color: ${props => props.theme.colors.background}dd;
  backdrop-filter: blur(8px);
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.foreground};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0;
  
  @media (min-width: 1024px) {
    width: 3rem;
    height: 3rem;
  }
  
  ${CarouselContainer}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.background};
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    
    @media (min-width: 1024px) {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

const PrevButton = styled(CarouselNavButton)`
  left: 1rem;
  
  @media (min-width: 1024px) {
    left: 1.5rem;
  }
`;

const NextButton = styled(CarouselNavButton)`
  right: 1rem;
  
  @media (min-width: 1024px) {
    right: 1.5rem;
  }
`;

const CarouselIndicators = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 20;
  
  @media (min-width: 1024px) {
    bottom: 1.5rem;
  }
`;

const Indicator = styled.button`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: ${props => props.$active ? props.theme.colors.background : props.theme.colors.background}66;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  @media (min-width: 1024px) {
    width: 0.625rem;
    height: 0.625rem;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.background}aa;
  }
`;

// Right Side - YouTube-style Sidebar
const SidebarContainer = styled.div`
  display: none;
  
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-height: 32rem;
    overflow-y: auto;
    padding-right: 0.5rem;
    
    @media (min-width: 1440px) {
      max-height: 36rem;
      gap: 0.875rem;
    }
    
    @media (min-width: 1920px) {
      max-height: 40rem;
      gap: 1rem;
    }
    
    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${props => props.theme.colors.muted};
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${props => props.theme.colors.mutedForeground};
      border-radius: 3px;
      
      &:hover {
        background: ${props => props.theme.colors.foreground};
      }
    }
  }
`;

const SidebarTitle = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 1440px) {
    font-size: 1.375rem;
    margin-bottom: 1.25rem;
  }
`;

const SidebarCard = styled(Link)`
  display: flex;
  gap: 0.625rem;
  text-decoration: none;
  color: inherit;
  padding: 0.375rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: background-color 0.2s;
  
  @media (min-width: 1440px) {
    gap: 0.75rem;
    padding: 0.5rem;
  }
  
  @media (min-width: 1920px) {
    gap: 0.875rem;
    padding: 0.625rem;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.muted};
  }
`;

const SidebarThumbnail = styled.div`
  position: relative;
  width: 8rem;
  height: 4.5rem;
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  
  @media (min-width: 1440px) {
    width: 9rem;
    height: 5rem;
  }
  
  @media (min-width: 1920px) {
    width: 10rem;
    height: 5.625rem;
  }
`;

const SidebarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
  
  ${SidebarCard}:hover & {
    transform: scale(1.05);
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const SidebarCardTitle = styled.h4`
  font-size: 0.8125rem;
  font-weight: ${props => props.theme.typography.fontWeights.medium};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.125rem;
  
  @media (min-width: 1440px) {
    font-size: 0.875rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 0.9375rem;
  }
`;

const SidebarMeta = styled.div`
  font-size: 0.6875rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 1440px) {
    font-size: 0.75rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 0.8125rem;
  }
`;

const HeroSection = ({ topArticles = [], popularArticles = [] }) => {
  const { t } = useTranslation();
  const { getArticleUrl } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const top5Articles = topArticles.slice(0, 5);
  const popular6to10 = popularArticles.slice(0, 10);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % top5Articles.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + top5Articles.length) % top5Articles.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play carousel
  useEffect(() => {
    if (top5Articles.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % top5Articles.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [top5Articles.length, isPaused]);

  if (top5Articles.length === 0) {
    return null;
  }

  return (
    <HeroContainer>
      {/* Left Side - Carousel */}
      <CarouselContainer
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <CarouselWrapper>
          <CarouselTrack $currentIndex={currentIndex}>
            {top5Articles.map((article, index) => {
              const articleSlug = article.slug;
              return (
                <CarouselSlide key={article._id || index}>
                  <Link to={getArticleUrl(articleSlug)} style={{ display: 'block', height: '100%' }}>
                    <CarouselImage src={article.imageUrl} alt={article.imageAlt || article.title} loading="lazy" />
                    <CarouselOverlay />
                    <CarouselContent>
                      <CarouselBadge>
                        <CategoryBadge category={article.category} color={article.categoryColor} />
                      </CarouselBadge>
                      <CarouselTitle>{article.title}</CarouselTitle>
                      <CarouselMeta>
                        <span>{article.author}</span>
                        <span>•</span>
                        <span>{article.readTime}</span>
                      </CarouselMeta>
                    </CarouselContent>
                  </Link>
                </CarouselSlide>
              );
            })}
          </CarouselTrack>
          
          {top5Articles.length > 1 && (
            <>
              <PrevButton onClick={prevSlide} aria-label={t('carousel.previous')}>
                <ChevronLeft />
              </PrevButton>
              <NextButton onClick={nextSlide} aria-label={t('carousel.next')}>
                <ChevronRight />
              </NextButton>
              <CarouselIndicators>
                {top5Articles.map((_, index) => (
                  <Indicator
                    key={index}
                    $active={index === currentIndex}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </CarouselIndicators>
            </>
          )}
        </CarouselWrapper>
      </CarouselContainer>

      {/* Right Side - YouTube-style Sidebar */}
      {popular6to10.length > 0 && (
        <SidebarContainer>
          <SidebarTitle>Popular Articles</SidebarTitle>
          {popular6to10.map((article, index) => (
              <SidebarCard key={article._id || index} to={getArticleUrl(article.slug)}>
                <SidebarThumbnail>
                  <SidebarImage src={article.imageUrl} alt={article.imageAlt || article.title} loading="lazy" />
                </SidebarThumbnail>
                <SidebarContent>
                  <SidebarCardTitle>{article.title}</SidebarCardTitle>
                  <SidebarMeta>
                    {article.author} • {article.readTime}
                  </SidebarMeta>
                </SidebarContent>
              </SidebarCard>
          ))}
        </SidebarContainer>
      )}
    </HeroContainer>
  );
};

export default HeroSection;

